/**
 * Countries with Flags Data
 * 
 * A comprehensive list of countries with their ISO codes and Unicode flag emojis.
 * This file provides a mapping between country names, ISO codes, and flag emojis
 * for use in country selection components.
 * 
 * Features:
 * - Full country names
 * - ISO 3166-1 alpha-2 country codes
 * - Unicode flag emojis (rendered from country codes)
 * - Searchable and filterable data structure
 * 
 * Note: China is excluded from this list as per project requirements
 */

export interface CountryData {
  name: string;
  code: string;
  flag: string;
}

/**
 * Converts a country ISO code to its corresponding flag emoji
 * @param countryCode - Two-letter ISO 3166-1 alpha-2 country code
 * @returns Unicode flag emoji
 */
export const getFlagEmoji = (countryCode: string): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

/**
 * List of countries with their ISO codes and flag emojis
 * Sorted alphabetically by country name
 */
export const COUNTRIES_WITH_FLAGS: CountryData[] = [
  { name: 'Afghanistan', code: 'AF', flag: getFlagEmoji('AF') },
  { name: 'Albania', code: 'AL', flag: getFlagEmoji('AL') },
  { name: 'Algeria', code: 'DZ', flag: getFlagEmoji('DZ') },
  { name: 'Andorra', code: 'AD', flag: getFlagEmoji('AD') },
  { name: 'Angola', code: 'AO', flag: getFlagEmoji('AO') },
  { name: 'Antigua and Barbuda', code: 'AG', flag: getFlagEmoji('AG') },
  { name: 'Argentina', code: 'AR', flag: getFlagEmoji('AR') },
  { name: 'Armenia', code: 'AM', flag: getFlagEmoji('AM') },
  { name: 'Australia', code: 'AU', flag: getFlagEmoji('AU') },
  { name: 'Austria', code: 'AT', flag: getFlagEmoji('AT') },
  { name: 'Azerbaijan', code: 'AZ', flag: getFlagEmoji('AZ') },
  { name: 'Bahamas', code: 'BS', flag: getFlagEmoji('BS') },
  { name: 'Bahrain', code: 'BH', flag: getFlagEmoji('BH') },
  { name: 'Bangladesh', code: 'BD', flag: getFlagEmoji('BD') },
  { name: 'Barbados', code: 'BB', flag: getFlagEmoji('BB') },
  { name: 'Belarus', code: 'BY', flag: getFlagEmoji('BY') },
  { name: 'Belgium', code: 'BE', flag: getFlagEmoji('BE') },
  { name: 'Belize', code: 'BZ', flag: getFlagEmoji('BZ') },
  { name: 'Benin', code: 'BJ', flag: getFlagEmoji('BJ') },
  { name: 'Bhutan', code: 'BT', flag: getFlagEmoji('BT') },
  { name: 'Bolivia', code: 'BO', flag: getFlagEmoji('BO') },
  { name: 'Bosnia and Herzegovina', code: 'BA', flag: getFlagEmoji('BA') },
  { name: 'Botswana', code: 'BW', flag: getFlagEmoji('BW') },
  { name: 'Brazil', code: 'BR', flag: getFlagEmoji('BR') },
  { name: 'Brunei', code: 'BN', flag: getFlagEmoji('BN') },
  { name: 'Bulgaria', code: 'BG', flag: getFlagEmoji('BG') },
  { name: 'Burkina Faso', code: 'BF', flag: getFlagEmoji('BF') },
  { name: 'Burundi', code: 'BI', flag: getFlagEmoji('BI') },
  { name: 'Cabo Verde', code: 'CV', flag: getFlagEmoji('CV') },
  { name: 'Cambodia', code: 'KH', flag: getFlagEmoji('KH') },
  { name: 'Cameroon', code: 'CM', flag: getFlagEmoji('CM') },
  { name: 'Canada', code: 'CA', flag: getFlagEmoji('CA') },
  { name: 'Central African Republic', code: 'CF', flag: getFlagEmoji('CF') },
  { name: 'Chad', code: 'TD', flag: getFlagEmoji('TD') },
  { name: 'Chile', code: 'CL', flag: getFlagEmoji('CL') },
  { name: 'Colombia', code: 'CO', flag: getFlagEmoji('CO') },
  { name: 'Comoros', code: 'KM', flag: getFlagEmoji('KM') },
  { name: 'Congo', code: 'CG', flag: getFlagEmoji('CG') },
  { name: 'Costa Rica', code: 'CR', flag: getFlagEmoji('CR') },
  { name: 'Croatia', code: 'HR', flag: getFlagEmoji('HR') },
  { name: 'Cuba', code: 'CU', flag: getFlagEmoji('CU') },
  { name: 'Cyprus', code: 'CY', flag: getFlagEmoji('CY') },
  { name: 'Czech Republic', code: 'CZ', flag: getFlagEmoji('CZ') },
  { name: 'Democratic Republic of the Congo', code: 'CD', flag: getFlagEmoji('CD') },
  { name: 'Denmark', code: 'DK', flag: getFlagEmoji('DK') },
  { name: 'Djibouti', code: 'DJ', flag: getFlagEmoji('DJ') },
  { name: 'Dominica', code: 'DM', flag: getFlagEmoji('DM') },
  { name: 'Dominican Republic', code: 'DO', flag: getFlagEmoji('DO') },
  { name: 'East Timor', code: 'TL', flag: getFlagEmoji('TL') },
  { name: 'Ecuador', code: 'EC', flag: getFlagEmoji('EC') },
  { name: 'Egypt', code: 'EG', flag: getFlagEmoji('EG') },
  { name: 'El Salvador', code: 'SV', flag: getFlagEmoji('SV') },
  { name: 'Equatorial Guinea', code: 'GQ', flag: getFlagEmoji('GQ') },
  { name: 'Eritrea', code: 'ER', flag: getFlagEmoji('ER') },
  { name: 'Estonia', code: 'EE', flag: getFlagEmoji('EE') },
  { name: 'Eswatini', code: 'SZ', flag: getFlagEmoji('SZ') },
  { name: 'Ethiopia', code: 'ET', flag: getFlagEmoji('ET') },
  { name: 'Fiji', code: 'FJ', flag: getFlagEmoji('FJ') },
  { name: 'Finland', code: 'FI', flag: getFlagEmoji('FI') },
  { name: 'France', code: 'FR', flag: getFlagEmoji('FR') },
  { name: 'Gabon', code: 'GA', flag: getFlagEmoji('GA') },
  { name: 'Gambia', code: 'GM', flag: getFlagEmoji('GM') },
  { name: 'Georgia', code: 'GE', flag: getFlagEmoji('GE') },
  { name: 'Germany', code: 'DE', flag: getFlagEmoji('DE') },
  { name: 'Ghana', code: 'GH', flag: getFlagEmoji('GH') },
  { name: 'Greece', code: 'GR', flag: getFlagEmoji('GR') },
  { name: 'Grenada', code: 'GD', flag: getFlagEmoji('GD') },
  { name: 'Guatemala', code: 'GT', flag: getFlagEmoji('GT') },
  { name: 'Guinea', code: 'GN', flag: getFlagEmoji('GN') },
  { name: 'Guinea-Bissau', code: 'GW', flag: getFlagEmoji('GW') },
  { name: 'Guyana', code: 'GY', flag: getFlagEmoji('GY') },
  { name: 'Haiti', code: 'HT', flag: getFlagEmoji('HT') },
  { name: 'Honduras', code: 'HN', flag: getFlagEmoji('HN') },
  { name: 'Hungary', code: 'HU', flag: getFlagEmoji('HU') },
  { name: 'Iceland', code: 'IS', flag: getFlagEmoji('IS') },
  { name: 'India', code: 'IN', flag: getFlagEmoji('IN') },
  { name: 'Indonesia', code: 'ID', flag: getFlagEmoji('ID') },
  { name: 'Iran', code: 'IR', flag: getFlagEmoji('IR') },
  { name: 'Iraq', code: 'IQ', flag: getFlagEmoji('IQ') },
  { name: 'Ireland', code: 'IE', flag: getFlagEmoji('IE') },
  { name: 'Israel', code: 'IL', flag: getFlagEmoji('IL') },
  { name: 'Italy', code: 'IT', flag: getFlagEmoji('IT') },
  { name: 'Ivory Coast', code: 'CI', flag: getFlagEmoji('CI') },
  { name: 'Jamaica', code: 'JM', flag: getFlagEmoji('JM') },
  { name: 'Japan', code: 'JP', flag: getFlagEmoji('JP') },
  { name: 'Jordan', code: 'JO', flag: getFlagEmoji('JO') },
  { name: 'Kazakhstan', code: 'KZ', flag: getFlagEmoji('KZ') },
  { name: 'Kenya', code: 'KE', flag: getFlagEmoji('KE') },
  { name: 'Kiribati', code: 'KI', flag: getFlagEmoji('KI') },
  { name: 'Kosovo', code: 'XK', flag: getFlagEmoji('XK') },
  { name: 'Kuwait', code: 'KW', flag: getFlagEmoji('KW') },
  { name: 'Kyrgyzstan', code: 'KG', flag: getFlagEmoji('KG') },
  { name: 'Laos', code: 'LA', flag: getFlagEmoji('LA') },
  { name: 'Latvia', code: 'LV', flag: getFlagEmoji('LV') },
  { name: 'Lebanon', code: 'LB', flag: getFlagEmoji('LB') },
  { name: 'Lesotho', code: 'LS', flag: getFlagEmoji('LS') },
  { name: 'Liberia', code: 'LR', flag: getFlagEmoji('LR') },
  { name: 'Libya', code: 'LY', flag: getFlagEmoji('LY') },
  { name: 'Liechtenstein', code: 'LI', flag: getFlagEmoji('LI') },
  { name: 'Lithuania', code: 'LT', flag: getFlagEmoji('LT') },
  { name: 'Luxembourg', code: 'LU', flag: getFlagEmoji('LU') },
  { name: 'Madagascar', code: 'MG', flag: getFlagEmoji('MG') },
  { name: 'Malawi', code: 'MW', flag: getFlagEmoji('MW') },
  { name: 'Malaysia', code: 'MY', flag: getFlagEmoji('MY') },
  { name: 'Maldives', code: 'MV', flag: getFlagEmoji('MV') },
  { name: 'Mali', code: 'ML', flag: getFlagEmoji('ML') },
  { name: 'Malta', code: 'MT', flag: getFlagEmoji('MT') },
  { name: 'Marshall Islands', code: 'MH', flag: getFlagEmoji('MH') },
  { name: 'Mauritania', code: 'MR', flag: getFlagEmoji('MR') },
  { name: 'Mauritius', code: 'MU', flag: getFlagEmoji('MU') },
  { name: 'Mexico', code: 'MX', flag: getFlagEmoji('MX') },
  { name: 'Micronesia', code: 'FM', flag: getFlagEmoji('FM') },
  { name: 'Moldova', code: 'MD', flag: getFlagEmoji('MD') },
  { name: 'Monaco', code: 'MC', flag: getFlagEmoji('MC') },
  { name: 'Mongolia', code: 'MN', flag: getFlagEmoji('MN') },
  { name: 'Montenegro', code: 'ME', flag: getFlagEmoji('ME') },
  { name: 'Morocco', code: 'MA', flag: getFlagEmoji('MA') },
  { name: 'Mozambique', code: 'MZ', flag: getFlagEmoji('MZ') },
  { name: 'Myanmar', code: 'MM', flag: getFlagEmoji('MM') },
  { name: 'Namibia', code: 'NA', flag: getFlagEmoji('NA') },
  { name: 'Nauru', code: 'NR', flag: getFlagEmoji('NR') },
  { name: 'Nepal', code: 'NP', flag: getFlagEmoji('NP') },
  { name: 'Netherlands', code: 'NL', flag: getFlagEmoji('NL') },
  { name: 'New Zealand', code: 'NZ', flag: getFlagEmoji('NZ') },
  { name: 'Nicaragua', code: 'NI', flag: getFlagEmoji('NI') },
  { name: 'Niger', code: 'NE', flag: getFlagEmoji('NE') },
  { name: 'Nigeria', code: 'NG', flag: getFlagEmoji('NG') },
  { name: 'North Korea', code: 'KP', flag: getFlagEmoji('KP') },
  { name: 'North Macedonia', code: 'MK', flag: getFlagEmoji('MK') },
  { name: 'Norway', code: 'NO', flag: getFlagEmoji('NO') },
  { name: 'Oman', code: 'OM', flag: getFlagEmoji('OM') },
  { name: 'Pakistan', code: 'PK', flag: getFlagEmoji('PK') },
  { name: 'Palau', code: 'PW', flag: getFlagEmoji('PW') },
  { name: 'Palestine', code: 'PS', flag: getFlagEmoji('PS') },
  { name: 'Panama', code: 'PA', flag: getFlagEmoji('PA') },
  { name: 'Papua New Guinea', code: 'PG', flag: getFlagEmoji('PG') },
  { name: 'Paraguay', code: 'PY', flag: getFlagEmoji('PY') },
  { name: 'Peru', code: 'PE', flag: getFlagEmoji('PE') },
  { name: 'Philippines', code: 'PH', flag: getFlagEmoji('PH') },
  { name: 'Poland', code: 'PL', flag: getFlagEmoji('PL') },
  { name: 'Portugal', code: 'PT', flag: getFlagEmoji('PT') },
  { name: 'Qatar', code: 'QA', flag: getFlagEmoji('QA') },
  { name: 'Romania', code: 'RO', flag: getFlagEmoji('RO') },
  { name: 'Russia', code: 'RU', flag: getFlagEmoji('RU') },
  { name: 'Rwanda', code: 'RW', flag: getFlagEmoji('RW') },
  { name: 'Saint Kitts and Nevis', code: 'KN', flag: getFlagEmoji('KN') },
  { name: 'Saint Lucia', code: 'LC', flag: getFlagEmoji('LC') },
  { name: 'Saint Vincent and the Grenadines', code: 'VC', flag: getFlagEmoji('VC') },
  { name: 'Samoa', code: 'WS', flag: getFlagEmoji('WS') },
  { name: 'San Marino', code: 'SM', flag: getFlagEmoji('SM') },
  { name: 'Sao Tome and Principe', code: 'ST', flag: getFlagEmoji('ST') },
  { name: 'Saudi Arabia', code: 'SA', flag: getFlagEmoji('SA') },
  { name: 'Senegal', code: 'SN', flag: getFlagEmoji('SN') },
  { name: 'Serbia', code: 'RS', flag: getFlagEmoji('RS') },
  { name: 'Seychelles', code: 'SC', flag: getFlagEmoji('SC') },
  { name: 'Sierra Leone', code: 'SL', flag: getFlagEmoji('SL') },
  { name: 'Singapore', code: 'SG', flag: getFlagEmoji('SG') },
  { name: 'Slovakia', code: 'SK', flag: getFlagEmoji('SK') },
  { name: 'Slovenia', code: 'SI', flag: getFlagEmoji('SI') },
  { name: 'Solomon Islands', code: 'SB', flag: getFlagEmoji('SB') },
  { name: 'Somalia', code: 'SO', flag: getFlagEmoji('SO') },
  { name: 'South Africa', code: 'ZA', flag: getFlagEmoji('ZA') },
  { name: 'South Korea', code: 'KR', flag: getFlagEmoji('KR') },
  { name: 'South Sudan', code: 'SS', flag: getFlagEmoji('SS') },
  { name: 'Spain', code: 'ES', flag: getFlagEmoji('ES') },
  { name: 'Sri Lanka', code: 'LK', flag: getFlagEmoji('LK') },
  { name: 'Sudan', code: 'SD', flag: getFlagEmoji('SD') },
  { name: 'Suriname', code: 'SR', flag: getFlagEmoji('SR') },
  { name: 'Sweden', code: 'SE', flag: getFlagEmoji('SE') },
  { name: 'Switzerland', code: 'CH', flag: getFlagEmoji('CH') },
  { name: 'Syria', code: 'SY', flag: getFlagEmoji('SY') },
  { name: 'Taiwan', code: 'TW', flag: getFlagEmoji('TW') },
  { name: 'Tajikistan', code: 'TJ', flag: getFlagEmoji('TJ') },
  { name: 'Tanzania', code: 'TZ', flag: getFlagEmoji('TZ') },
  { name: 'Thailand', code: 'TH', flag: getFlagEmoji('TH') },
  { name: 'Togo', code: 'TG', flag: getFlagEmoji('TG') },
  { name: 'Tonga', code: 'TO', flag: getFlagEmoji('TO') },
  { name: 'Trinidad and Tobago', code: 'TT', flag: getFlagEmoji('TT') },
  { name: 'Tunisia', code: 'TN', flag: getFlagEmoji('TN') },
  { name: 'Turkey', code: 'TR', flag: getFlagEmoji('TR') },
  { name: 'Turkmenistan', code: 'TM', flag: getFlagEmoji('TM') },
  { name: 'Tuvalu', code: 'TV', flag: getFlagEmoji('TV') },
  { name: 'Uganda', code: 'UG', flag: getFlagEmoji('UG') },
  { name: 'Ukraine', code: 'UA', flag: getFlagEmoji('UA') },
  { name: 'United Arab Emirates', code: 'AE', flag: getFlagEmoji('AE') },
  { name: 'United Kingdom', code: 'GB', flag: getFlagEmoji('GB') },
  { name: 'United States', code: 'US', flag: getFlagEmoji('US') },
  { name: 'Uruguay', code: 'UY', flag: getFlagEmoji('UY') },
  { name: 'Uzbekistan', code: 'UZ', flag: getFlagEmoji('UZ') },
  { name: 'Vanuatu', code: 'VU', flag: getFlagEmoji('VU') },
  { name: 'Vatican City', code: 'VA', flag: getFlagEmoji('VA') },
  { name: 'Venezuela', code: 'VE', flag: getFlagEmoji('VE') },
  { name: 'Vietnam', code: 'VN', flag: getFlagEmoji('VN') },
  { name: 'Yemen', code: 'YE', flag: getFlagEmoji('YE') },
  { name: 'Zambia', code: 'ZM', flag: getFlagEmoji('ZM') },
  { name: 'Zimbabwe', code: 'ZW', flag: getFlagEmoji('ZW') },
];

/**
 * Get country data by country name
 * @param countryName - Full country name
 * @returns CountryData object or undefined if not found
 */
export const getCountryByName = (countryName: string): CountryData | undefined => {
  return COUNTRIES_WITH_FLAGS.find(
    (country) => country.name.toLowerCase() === countryName.toLowerCase()
  );
};

/**
 * Get country data by ISO code
 * @param code - Two-letter ISO 3166-1 alpha-2 country code
 * @returns CountryData object or undefined if not found
 */
export const getCountryByCode = (code: string): CountryData | undefined => {
  return COUNTRIES_WITH_FLAGS.find(
    (country) => country.code.toUpperCase() === code.toUpperCase()
  );
};

